import React, { useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiService } from '../services/api';
import InlineFeedback from '../components/InlineFeedback';
import { useFeedback } from '../hooks/useFeedback';
import { ADMIN_LOGIN_NOTICE_KEY } from '../services/adminSession';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => {
    // Pre-fill with stored admin email if available
    return localStorage.getItem('admin_email') || '';
  });
  const [isVerifying, setIsVerifying] = useState(false);
  const hasVerified = useRef(false);
  const feedback = useFeedback(0);
  const [storedNotice] = useState(() => {
    const notice = sessionStorage.getItem(ADMIN_LOGIN_NOTICE_KEY);
    sessionStorage.removeItem(ADMIN_LOGIN_NOTICE_KEY);
    return notice;
  });
  const notice = (location.state as { notice?: string } | null)?.notice || storedNotice;

  // Check if we have a token in the URL for verification
  const token = searchParams.get('token');

  const sendMagicLinkMutation = useMutation({
    mutationFn: apiService.sendMagicLink,
    onSuccess: () => {
      feedback.showSuccess('Sign-in link sent. Check your email.');
      // Don't clear email - user might need to resend
    },
    onError: (error: any) => {
      feedback.showError(error.response?.data?.message || 'Failed to send sign-in link.');
    },
  });

  const verifyMagicLinkMutation = useMutation({
    mutationFn: apiService.verifyMagicLink,
    onSuccess: (data) => {
      // Store admin session token
      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_email', data.email);
      navigate('/admin');
    },
    onError: (error: any) => {
      feedback.showError(error.response?.data?.message || 'This link is invalid or expired.');
      setIsVerifying(false);
    },
  });

  React.useEffect(() => {
    if (token && !hasVerified.current) {
      hasVerified.current = true;
      setIsVerifying(true);
      verifyMagicLinkMutation.mutate(token);
    }
  }, [token, verifyMagicLinkMutation]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      feedback.clear();
      sendMagicLinkMutation.mutate({ email });
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-body-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-body-color">Checking your link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-body-bg">
      <div className="bg-widget-bg rounded-lg border border-secondary p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-heading mb-6 text-center">Admin Login</h1>

        {notice && (
          <InlineFeedback feedback={{ kind: 'info', message: notice }} className="mb-4" />
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field w-full text-center text-base py-2"
              placeholder="admin@example.com"
              required
            />
            <p className="mt-2 text-sm text-secondary text-center">
              Enter your admin email. We send a sign-in link to it.
            </p>
          </div>

          <InlineFeedback feedback={feedback.feedback} />

          <button
            type="submit"
            disabled={sendMagicLinkMutation.isPending}
            className="btn-primary w-full"
          >
            {sendMagicLinkMutation.isPending ? 'Sending...' : 'Send Sign-in Link'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a href="/" className="text-primary hover:text-primary-hover text-sm">
            Back to Dashboard
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
